/**
 * Central registry of API paths. Mirrors the NestJS controllers in
 * `server/src/modules/*`. Keeping these in one file means a backend rename
 * only requires one edit in the client, and surfaces mismatches fast.
 */
export const apiRoutes = {
  auth: {
    callback: '/auth/callback',
  },
  accounts: {
    me: '/accounts/me',
    parentPin: '/accounts/me/parent-pin',
    verifyParentPin: '/accounts/me/parent-pin/verify',
    affiliate: '/accounts/me/affiliate',
    affiliateRequest: '/accounts/me/affiliate/request',
    affiliateSlug: '/accounts/me/affiliate/slug',
    referrals: '/accounts/me/referrals',
  },
  affiliate: {
    // Public click beacon (no auth). Fired at boot when a `?ref=<code>` is
    // captured — see `lib/referral.ts` + server `affiliate-clicks.controller`.
    click: '/affiliate/clicks',
  },
  readers: {
    list: '/readers',
    create: '/readers',
    one: (id: string) => `/readers/${id}`,
    labels: (id: string) => `/readers/${id}/labels`,
  },
  books: {
    list: '/books',
    one: (id: string) => `/books/${id}`,
    similar: (id: string) => `/books/${id}/similar`,
    descriptionAudio: (id: string) => `/books/${id}/description-audio`,
    cover: (id: string) => `/books/${id}/cover`,
    vocabularyBookmark: (id: string) => `/books/${id}/vocabulary-bookmark`,
    embedding: (id: string) => `/books/${id}/embedding`,
    labels: (id: string) => `/books/${id}/labels`,
    label: (id: string, labelId: string) => `/books/${id}/labels/${labelId}`,
    activities: (id: string) => `/books/${id}/activities`,
    accountMetadata: (id: string) => `/books/${id}/account-metadata`,
  },
  activities: {
    one: (id: string) => `/activities/${id}`,
  },
  adminBooks: {
    list: '/admin/books',
  },
  articles: {
    list: '/articles',
    featured: '/articles/featured',
    bySlug: (slug: string) => `/articles/${encodeURIComponent(slug)}`,
    create: '/articles',
    one: (id: string) => `/articles/${id}`,
    thumbnail: (id: string) => `/articles/${id}/thumbnail`,
  },
  adminArticles: {
    list: '/admin/articles',
    one: (id: string) => `/admin/articles/${id}`,
  },
  quizzes: {
    forBook: (bookId: string) => `/books/${bookId}/quiz`,
    forArticle: (slug: string) => `/articles/${encodeURIComponent(slug)}/quiz`,
    one: (id: string) => `/quizzes/${id}`,
    startAttempt: (quizId: string) => `/quizzes/${quizId}/attempts`,
    recordAnswer: (attemptId: string) => `/attempts/${attemptId}/answers`,
    completeAttempt: (attemptId: string) => `/attempts/${attemptId}/complete`,
    readerStats: (readerId: string | number) => `/readers/${readerId}/stats`,
  },
  adminQuizzes: {
    forBook: (bookId: string) => `/admin/books/${bookId}/quiz`,
    forArticle: (articleId: string) => `/admin/articles/${articleId}/quiz`,
    one: (id: string) => `/admin/quizzes/${id}`,
    create: '/admin/quizzes',
    questions: (id: string) => `/admin/quizzes/${id}/questions`,
  },
  labels: {
    list: '/labels',
    create: '/labels',
  },
  search: {
    query: '/search',
  },
  learn: {
    ask: '/learn',
  },
  prompts: {
    forBook: (bookId: string) => `/books/${bookId}/prompts`,
    one: (id: string) => `/prompts/${id}`,
  },
  bookmarks: {
    list: '/bookmarks',
    create: '/bookmarks',
    one: (id: string) => `/bookmarks/${id}`,
    check: '/bookmarks/check',
  },
  loggedBooks: {
    list: '/logged-books',
    create: '/logged-books',
    one: (id: string) => `/logged-books/${id}`,
    lookup: '/logged-books/lookup',
    cover: '/logged-books/cover',
  },
  reviews: {
    forBook: (bookId: string) => `/books/${bookId}/reviews`,
    forReader: (readerId: string) => `/readers/${readerId}/reviews`,
    mine: '/reviews/mine',
    create: '/reviews',
    one: (id: string) => `/reviews/${id}`,
  },
  adminReviews: {
    list: '/admin/reviews',
    one: (id: string) => `/admin/reviews/${id}`,
  },
  readingGoals: {
    list: '/reading-goals',
    active: '/reading-goals/active',
    one: (id: string) => `/reading-goals/${id}`,
    create: '/reading-goals',
    markDay: (id: string) => `/reading-goals/${id}/days`,
    abandon: (id: string) => `/reading-goals/${id}/abandon`,
  },
  coins: {
    summary: (readerId: string | number) => `/readers/${readerId}/coins`,
    history: (readerId: string | number) => `/readers/${readerId}/coins/history`,
    quizEarned: (readerId: string | number, quizId: string) =>
      `/readers/${readerId}/coins/quiz/${quizId}`,
    adminGrant: (readerId: string | number) => `/admin/readers/${readerId}/coins/grants`,
    adminAdjust: (readerId: string | number) => `/admin/readers/${readerId}/coins/adjustments`,
    adminHistory: (readerId: string | number) => `/admin/readers/${readerId}/coins/history`,
  },
  billing: {
    subscription: '/billing/subscription',
    // Live-from-Stripe discount for the subscription card (separate from the
    // cheap mirrored `subscription` read). Nullable payload.
    discount: '/billing/subscription/discount',
    checkoutSession: '/billing/checkout-session',
    portalSession: '/billing/portal-session',
  },
  payouts: {
    onboarding: '/billing/affiliate/connect/onboarding',
    updateLink: '/billing/affiliate/connect/update-link',
    status: '/billing/affiliate/connect/status',
    summary: '/billing/affiliate/accruals/summary',
    payouts: '/billing/affiliate/payouts',
  },
  adminAffiliates: {
    list: '/admin/affiliates',
    triggerPayout: (accountId: string) => `/admin/affiliates/${accountId}/payout`,
  },
  adminAffiliateRequests: {
    list: '/admin/affiliate-requests',
    decide: (accountId: string) => `/admin/affiliate-requests/${accountId}`,
  },
  adminMonitoring: {
    overview: '/admin/monitoring/overview',
    accounts: '/admin/monitoring/accounts',
  },
  adminBilling: {
    reconcile: (accountId: string) => `/admin/billing/accounts/${accountId}/reconcile`,
    plan: (accountId: string) => `/admin/billing/accounts/${accountId}/plan`,
  },
  games: {
    submitScore: '/games/scores',
    leaderboard: '/games/leaderboard',
    leaderboardSummary: '/games/leaderboard/summary',
  },
  adminSubscribers: {
    list: '/admin/subscribers',
    create: '/admin/subscribers',
    one: (id: string) => `/admin/subscribers/${id}`,
    assignList: (id: string) => `/admin/subscribers/${id}/list`,
    reactivate: (id: string) => `/admin/subscribers/${id}/reactivate`,
    import: '/admin/subscribers/import',
    bulkDelete: '/admin/subscribers/bulk-delete',
  },
  adminSubscriberLists: {
    list: '/admin/subscriber-lists',
    create: '/admin/subscriber-lists',
    one: (id: string) => `/admin/subscriber-lists/${id}`,
    members: (id: string) => `/admin/subscriber-lists/${id}/subscribers`,
  },
  adminEmailTemplates: {
    list: '/admin/email-templates',
    create: '/admin/email-templates',
    one: (id: string) => `/admin/email-templates/${id}`,
  },
  adminBroadcasts: {
    list: '/admin/broadcasts',
    create: '/admin/broadcasts',
    one: (id: string) => `/admin/broadcasts/${id}`,
    spamCheck: (id: string) => `/admin/broadcasts/${id}/spam-check`,
    test: (id: string) => `/admin/broadcasts/${id}/test`,
    send: (id: string) => `/admin/broadcasts/${id}/send`,
    cancel: (id: string) => `/admin/broadcasts/${id}/cancel`,
  },
  adminAutomations: {
    list: '/admin/automations',
    create: '/admin/automations',
    /** Static field/operator allowlist — must stay above `one` in the router. */
    conditionSchema: '/admin/automations/condition-schema',
    one: (id: string) => `/admin/automations/${id}`,
    preview: (id: string) => `/admin/automations/${id}/preview`,
    enable: (id: string) => `/admin/automations/${id}/enable`,
    disable: (id: string) => `/admin/automations/${id}/disable`,
    test: (id: string) => `/admin/automations/${id}/test`,
    runNow: (id: string) => `/admin/automations/${id}/run-now`,
  },
} as const;
